package com.swetdot.swetgram.ui.chat

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.Alignment
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.window.Dialog
import com.swetdot.swetgram.ui.design.Radius
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.SwetIcon
import com.swetdot.swetgram.ui.design.SwetDialog
import com.swetdot.swetgram.ui.design.SwetPressFeedback
import com.swetdot.swetgram.ui.design.TouchTarget
import com.swetdot.swetgram.ui.design.swetPressable
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens

/**
 * The action-sheet shell the messaging screens share -- SDS §7.1's `radius.xl`, on the main
 * surface, at the screen margin.
 *
 * Built on `androidx.compose.ui.window.Dialog` rather than Material3's `AlertDialog`, because
 * `AlertDialog` lays its actions out as a horizontal row of text buttons and the design system's
 * buttons are full-width by definition -- SDS §11.1 gives no compact variant.
 *
 * It lives in `ui/chat` rather than in either screen because both the chat list's row menu
 * (SPS §10.5-10.7, §15.3) and the conversation's message menu (SDS §12.5) are built from it, and
 * `ui/chats` already depends on this package for its time formatting. A third hand-rolled copy is
 * what this file exists to prevent: there were two before it, and the paddings had already drifted.
 *
 * TODO(design-system): this belongs in `ui/design` as a `SwetDialog`, alongside the buttons it is
 * always used with. It is here because `ui/design` was not this change's to edit, and a copy in
 * `ui/profile/ProfileScreen.kt` is still outstanding.
 */
@Composable
internal fun ChatDialog(
    onDismiss: () -> Unit,
    content: @Composable ColumnScope.() -> Unit,
) {
    SwetDialog(onDismiss = onDismiss, content = content)
}

/**
 * One entry in a [ChatDialog].
 *
 * SDS §12.5 requires destructive actions to be visually separated; [destructive] paints the label
 * in `danger`, and §22.2's rule that colour is never the only signal is satisfied by the words --
 * "Delete chat" says what it is without being red.
 */
@Composable
internal fun ChatActionRow(
    text: String,
    onClick: () -> Unit,
    destructive: Boolean = false,
    icon: ImageVector? = null,
) {
    val colors = SwetgramThemeTokens.colors
    val contentColor = if (destructive) colors.danger else colors.textPrimary
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(Radius.sm))
            // `swetPressable` with the STATE LAYER, not `clickable`. SwetgramTheme installs
            // MaterialTheme, so `LocalIndication` is populated and a bare `clickable` inherits
            // Material's ripple -- an expanding circle whose duration is Material's rather than
            // §19.2's, and which lands differently on this row than on a 44dp icon button.
            // SwetMotion documents exactly why that is wrong; §25.1's "visible but subtle" press
            // is a state layer, and on a full-width row a scale would move the whole sheet.
            .swetPressable(onClick = onClick, feedback = SwetPressFeedback.StateLayer)
            // A minimum rather than a height, so a large system font grows the row.
            .defaultMinSize(minHeight = TouchTarget.recommended)
            .padding(horizontal = Space.one, vertical = Space.one + Space.half),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        if (icon != null) {
            SwetIcon(
                icon = icon,
                contentDescription = null,
                tint = contentColor,
                size = Sizes.iconMedium,
            )
            Spacer(Modifier.width(Space.two))
        }
        Text(
            text = text,
            style = MaterialTheme.typography.bodyMedium,
            color = contentColor,
        )
    }
}
